import * as React from 'react';
import { Theme } from 'helpers/theme';
import { createUseStyles } from 'react-jss';
import { useRouter } from 'found';
import { createFragmentContainer, graphql } from 'react-relay';
import { SimulatorHistoryItem_result$data as SimulatorHistoryItem_result } from './__generated__/SimulatorHistoryItem_result.graphql';
import SimulatorImage from './SimulatorImage';
import { getStatusInfo } from '../../utils/helpers';

const useStyles = createUseStyles((theme: Theme) => ({
  box: {
    borderRadius: '9px',
    background: '#18181B',
    padding: '7px',
    gap: '10px',
    display: 'grid',
    gridTemplateColumns: '100px 1fr',
    border: '1px solid #2A2A2D',
    '&:hover': {
      cursor: 'pointer',
      background: 'rgba(43, 43, 43, 0.8)'
    },
    color: 'grey'
  },
  result: {
    display: 'flex',
    flexDirection: 'row',
    gap: '15px',
    alignItems: 'center',
    marginRight: '12px',
    justifyContent: 'flex-end'
  },
  resultText: {
    textAlign: 'right',
    fontSize: theme.fontSizes.small,
    fontWeight: '300'
  },
  simulatorImage: {
    width: '80px',
    borderRadius: '7px',
    overflow: 'hidden'
  },
  correctStatus: {
    textAlign: 'right',
    fontSize: 13,
    fontWeight: '500'
  },
  textContainer: {
    display: 'flex',
    flexDirection: 'column'
  }
}));

interface Props {
  result: SimulatorHistoryItem_result;
  onClick: () => void;
  onLoad?: () => void;
  selected?: boolean;
}

function SimulatorHistoryItem({ result, onLoad, onClick, selected }: Props) {
  const styles = useStyles();

  const { threatText, threatIcon, correctText, correctColor } = getStatusInfo(
    result.identifiedStatus
  );

  return (
    <div
      className={styles.box}
      onClick={onClick}
      style={
        selected
          ? {
              background: '#343446',
              border: '1px solid #205BF5',
              color: 'white'
            }
          : {}
      }
    >
      <div className={styles.simulatorImage}>
        <SimulatorImage
          width={80}
          height={50}
          quality={10}
          backgroundURL={result.image.normal.images.topURL}
          threatURL={result.image.threat?.threatXray.images.topURL}
          position={result.image.threat?.topPosition}
          onLoad={onLoad}
        />
      </div>
      <div className={styles.result}>
        <div className={styles.textContainer}>
          <div className={styles.correctStatus}>{correctText}</div>
          <div className={styles.resultText}>{threatText}</div>
        </div>

        {threatIcon}
      </div>
    </div>
  );
}

const SimulatorHistoryFrag = createFragmentContainer(SimulatorHistoryItem, {
  result: graphql`
    fragment SimulatorHistoryItem_result on ImageResult {
      uuid
      start
      end
      isCorrect
      identifiedStatus
      identification {
        isSearch
      }
      image {
        normal {
          images {
            topURL
          }
        }
        threat {
          threatXray {
            images {
              topURL
            }
          }
          topPosition {
            x
            y
            scale
            rotation
          }
        }
        alwaysSearch
      }
    }
  `
});

export default SimulatorHistoryFrag;
